<template>
  <RevSkeleton
    v-if="!hasFetchedOnce"
    height="200px"
    shape="rectangle"
    width="100%"
  />
  <ProfileCard
    v-else
    data-qa="billing-address-card"
    :title="i18n(translations.header)"
    @edit="openModal(modalName)"
  >
    <AddressComponent
      v-if="hasAddress"
      :address="displayAddress"
      localize-name-order
      with-details
    />
    <p v-else data-test="empty-state">
      {{ i18n(translations.emptyAddress) }}
    </p>
  </ProfileCard>
  <RevDialog
    :name="modalName"
    :title="i18n(translations.modalTitle)"
    @close="resetValidationErrors"
  >
    <template #body>
      <AddressForm
        :address="data || null"
        :api-errors
        :country-options
        :is-loading="isFormLoading"
        @submit="updateAddress"
      />
    </template>
  </RevDialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import {
  getBillingAddress,
  postBillingAddress,
} from '@backmarket/http-api/src/api-specs-after-sale-experience/client/billing-address'
import { HttpEvent } from '@backmarket/http-api/src/utils/types'
import AddressComponent from '@backmarket/nuxt-module-address/Address.vue'
import type { Address } from '@backmarket/nuxt-module-address/address'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useSWRAsyncData } from '@backmarket/nuxt-module-http-v2/useSWRAsyncData'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevDialog } from '@ds/components/Dialog'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevSkeleton } from '@ds/components/Skeleton'

import { SALES_CUSTOMER_CARE_SCOPE } from '@/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useSalesCustomerCareLogger } from '@/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'

import ProfileCard from '../../ProfileCard/ProfileCard.vue'
import type { FormValues } from '../components/AddressForm/AddressForm.types'
import AddressForm from '../components/AddressForm/AddressForm.vue'
import { useMarketplaceCountryCodes } from '../composables/useMarketplaceCountryCodes'
import { useValidationErrors } from '../composables/useValidationErrors'
import { addressIsEmpty } from '../utils/addressIsEmpty'
import { isValidationError } from '../utils/isValidationError'

import translations from './BillingAddressCard.translations'

const i18n = useI18n()
const { logHttpOnEvent } = useSalesCustomerCareLogger()
const { openErrorToast, openSuccessToast } = useTheToast()
const marketplaceCountryCodes = useMarketplaceCountryCodes()

const modalName = 'PROFILE_BILLING_ADDRESS'

const apiErrors = ref<{ [key: string]: string }>({})
const isFormLoading = ref<boolean>(false)

const { data, hasFetchedOnce, refresh } = useSWRAsyncData(
  'billingAddress',
  () =>
    $httpFetch(getBillingAddress, {
      onEvent: (event, context) => {
        logHttpOnEvent({
          event,
          context,
          errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.myProfile} Error when fetching customer billing address`,
        })
        if (event === HttpEvent.Fail) {
          if (context.error && context.error.status !== 404) {
            openErrorToast()
          }
        }
      },
    }),
)

const hasAddress = computed(() => !addressIsEmpty(data.value || null))
const displayAddress = computed(() => data.value as unknown as Address)

type CountryOption = { label: string; value: string }
const countryOptions = computed<CountryOption[]>(() => {
  return data.value?.availableCountries
    ? data.value.availableCountries
        .map(
          ({ country_code: value, name }) =>
            ({
              label: (value && i18n.country(value)) || name,
              value,
            }) as CountryOption,
        )
        .sort((a, b) => a.label.localeCompare(b.label))
    : marketplaceCountryCodes
})

function transformRequestData(formValues: FormValues): Record<string, string> {
  return removeEmptyValuesInObject(formValues) as Record<string, string>
}

function resetValidationErrors(): void {
  apiErrors.value = {}
}

// eslint-disable-next-line consistent-return
async function updateAddress(formValues: FormValues) {
  resetValidationErrors()
  isFormLoading.value = true

  try {
    await $httpFetch(postBillingAddress, {
      body: transformRequestData(formValues),
      onEvent: (event, context) =>
        logHttpOnEvent({
          event,
          context,
          errorDetail: {
            request_body: transformRequestData(formValues),
          },
          errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.myProfile} Error when updating customer billing address`,
        }),
    })
    openSuccessToast({ title: i18n(translations.updateSuccess) })
    await refresh()
    closeModal(modalName)
  } catch (err) {
    if (isValidationError(err)) {
      apiErrors.value = useValidationErrors(err.errors, {
        POSTAL_CODE_INVALID: translations.validationErrorPostalCodeInvalid,
      })
    } else {
      openErrorToast()
    }
  } finally {
    isFormLoading.value = false
  }
}
</script>
